import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FeatureGate } from '@/components/ui/FeatureGate';
import {
  useScripts,
  useScriptScoringCoverage,
  ScriptsWorkbench,
  ScriptScoringOffNotice,
} from '@/features/scripts';
import type { ScriptsTab } from '@/features/scripts';

interface AnalysisSetupScriptsSearch {
  tab?: ScriptsTab;
}

export const Route = createFileRoute('/analysis-setup/scripts')({
  validateSearch: (search: Record<string, unknown>): AnalysisSetupScriptsSearch => ({
    tab: search.tab === 'scripts' ? 'scripts' : undefined,
  }),
  component: () => (
    <FeatureGate flag="enableScripts">
      <AnalysisSetupScriptsStep />
    </FeatureGate>
  ),
});

// useScripts() is owned here (not inside ScriptsWorkbench) — it holds local
// useState, so a second internal call would diverge from this one instead of
// sharing state (same reasoning as the standalone Scripts page).
//
// The tab lives in the URL, same shape as the Criteria step: category bindings
// first, the scripts themselves second. 'bindings' is the default and stays out
// of the URL, so the step's own nav link never carries a redundant param.
function AnalysisSetupScriptsStep() {
  const scriptsHook = useScripts();
  // Coverage only for scripts that reach a project: with none, there is no
  // «where are they checked» to answer (#1093).
  const scoring = useScriptScoringCoverage(scriptsHook.scripts);
  const navigate = useNavigate();
  const { tab } = Route.useSearch();

  return (
    <ScriptsWorkbench
      scriptsHook={scriptsHook}
      scoringNotice={
        <ScriptScoringOffNotice
          className="mb-4"
          scoringOff={scoring.scoringOff}
          allOff={scoring.allOff}
          truncated={scoring.truncated}
          error={scoring.error}
        />
      }
      tab={tab ?? 'bindings'}
      onTabChange={(next) => {
        void navigate({
          to: '/analysis-setup/scripts',
          search: { tab: next === 'bindings' ? undefined : next },
          replace: true,
        });
      }}
    />
  );
}
